import React, {
	FC, useEffect, useMemo, useState,
} from 'react';
import {
	AvatarSize,
	BackArrowIcon,
	BanIcon,
	Button,
	ButtonAppearance,
	ButtonColor,
	CloseCircleIcon, AsyncComboboxAutocomplete, Dialog,
	Highlight,
	Label,
	Menu,
	handleErrorNotification,
	NetworkConnectionIcon,
	cn,
} from '@aleph/shared-components';
import { useLazyQuery, useMutation, useQuery } from '@apollo/client';
import {
	isEqual, orderBy,
} from 'lodash';
import { AnimatePresence, motion } from 'framer-motion';
import RedditEntityImage from 'wb-reddit/app/common/components/entityImage';
import debounce from 'debounce-promise';
import { Trans, useTranslation } from 'react-i18next';
import {
	AuthGraphSpotifyReportExistingGroupsDocument,
	AuthGraphReportSharingOptionsDocument,
	AuthGraphAdvertiserListQueryItems,
} from 'wb-spotify/graphql/authgraph/generated';
import { Loader } from 'rsuite';
import { ShareSpotifyReportDocument, Report } from 'wb-spotify/graphql/generated';
import { HandleUpdateRow } from 'wb-reddit/app/routes/hooks/useUpdateRows';
import { ReportRow } from 'wb-reddit/app/routes/reports/utils';
import { captureException } from '@sentry/react';

type Entity = { value: string; label: string };

interface SpotifyReportSharingModalProps {
	report: Report;
	handleUpdateRow: HandleUpdateRow<ReportRow>
	onClose: () => void;
}

const SpotifyReportSharingModal: FC<SpotifyReportSharingModalProps> = ({ report, onClose, handleUpdateRow }) => {
	const { t } = useTranslation();
	const ownerId = useMemo(() => report?.reportFiltersOptions?.[0]?.value?.[0], [report]);

	const [selectedEntities, setSelectedEntities] = useState<Entity[]>([]);
	const [publicGroupIds, setPublicGroupIds] = useState<string[]>([]);
	const [setReportAccess, { loading: saving }] = useMutation(ShareSpotifyReportDocument);

	const [getUsers] = useLazyQuery(
		AuthGraphReportSharingOptionsDocument,
	);
	const { data: groupsData, loading } = useQuery(AuthGraphSpotifyReportExistingGroupsDocument, {
		variables: {
			userGroupId: report.userGroupId ?? '',
		},
		fetchPolicy: 'no-cache',
	});

	useEffect(() => {
		const groups = groupsData?.userGroups?.items ?? [];
		const groupIds = groups.map(item => item.id);

		setSelectedEntities(curr => [...curr, ...groups.map(item => ({
			value: item.id,
			label: item.name,
		}))]);

		setPublicGroupIds((report?.userGroupId ?? [])?.filter(element => !groupIds.includes(element)));
	}, [groupsData]);

	const debouncedGetUsers = useMemo(() => debounce(getUsers, 300), []);

	const loadOptions = async (search: string) => {
		if (search.length < 3) {
			return [];
		}

		const { data: foundUsers, error } = await debouncedGetUsers({
			variables: {
				userGroupOwnerEmail: `*${search}*`,
			},
		});

		if (error) {
			captureException(error);

			handleErrorNotification(
				error,
				t(
					'sp: reports sharing autocomplete error',
					'Something went wrong while searching for sharing options',
				),
			);

			return [];
		}

		const data = [...(foundUsers?.privateGroups?.items ?? []), ...(foundUsers?.publicGroups?.items ?? [])];

		return data
			.map(item => ({
				value: item.id,
				label: item.name,
			}))
			.filter(item => !selectedEntities.some(selected => selected.value === item.value));
	};

	const onSelectEntity = (entity: Entity) => {
		setSelectedEntities(curr => [...curr, entity]);
	};

	const close = () => {
		setSelectedEntities([]);
		onClose();
	};

	const saveReportSharing = async () => {
		const privateGroupIds = selectedEntities.map(entity => entity.value);

		const { data, errors } = await setReportAccess({
			variables: {
				reportId:     report.reportId,
				userGroupIds: [...publicGroupIds, ...privateGroupIds],
			},
		});

		if (errors) {
			captureException(errors);

			handleErrorNotification(errors);
		}

		if (data?.updateReport) {
			handleUpdateRow({
				row: {
					...report,
					...data?.updateReport,
					id: report.reportId,
				} as unknown as ReportRow,
			});
		}

		close();
	};

	return (
		<Dialog
			onClose={close}
			isOpen
			className="min-h-[50vh] max-w-[600px]"
			data-testid="share-report-container"
		>
			<Dialog.Header
				preset={
					{
						prefix: <NetworkConnectionIcon />,
						title:  t('re: reports.share', 'Share this report'),
					}
				}
			/>
			<Dialog.Content>
				<AsyncComboboxAutocomplete
					inputProps={{
						placeholder: t('re: add users or teams', 'Add users or teams...'),
					}}
					size='large'
					disabled={loading}
					loadOptions={loadOptions}
					renderOption={({ option, searchQuery, onSelected }) => (
						<Menu.Item
							key={option.value}
							className="h-auto"
							onClick={(e) => {
								e.stopPropagation();
								onSelectEntity(option as Entity);
								onSelected();
							}}
						>
							<RedditEntityImage
								size={AvatarSize.ExtraSmall}
								entity={option as unknown as AuthGraphAdvertiserListQueryItems}
							/>
							<Label>
								<Highlight
									source={option.label ?? ''}
									target={searchQuery.trim()}
								>
									{s => <strong>{s}</strong>}
								</Highlight>
							</Label>
						</Menu.Item>
					)}
					NoOptionsComponent={({ search }) => {
						if ((search?.length ?? 0) < 3) {
							return <Menu.Item>{t('sc: input length', 'Input at least 3 characters')}</Menu.Item>;
						}

						return (<div className='max-w-[25vw] w-fit'>
							{
								<Trans
									i18nKey="sc: no match"
									defaults="<strong>No match.</strong> Please enter a full email address to add people outside your organization."
								>
									<strong>No match.</strong>
									Please enter a full email address to add people outside your organization.
								</Trans>
							}
						</div>);
					}}
				/>
				<div className="flex items-center justify-between h-[32px] px-[8px] text-[11px] text-gray-600 py-[8px]">
					<div>
						{t('re: report sharing modal contributor', 'CONTRIBUTOR')}
					</div>

					<div>
						{t('re: report sharing modal remove', 'REMOVE')}
					</div>
				</div>
				<div className="flex-1 overflow-y-auto">
					{(loading || saving) && (
						<div className="h-full flex justify-center items-center">
							<Loader size="md"/>
						</div>
					)}
					<AnimatePresence>
						{orderBy(selectedEntities, entity => (isEqual(entity.value, ownerId) ? -1 : 1)).map((entity) => (
							<motion.div
								key={`${entity?.value}_${entity?.label}`}
								initial={{ height: '48px' }}
								exit={{ height: 0 }}
								transition={{ duration: 0.1 }}
								className={cn(
									'flex overflow-hidden items-center w-full rounded h-[48px] px-[8px] gap-[8px] hover:bg-gray-100 border-b border-b-gray-200',
									{
										'pointer-events-none opacity-50': loading || saving,
									},
								)}
							>
								<RedditEntityImage entity={entity as unknown as AuthGraphAdvertiserListQueryItems} />
								<div className="flex-1 flex items-center gap-[8px] overflow-hidden">
									<div className="pointer-events-none text-[16px] text-gray-950 overflow-hidden
										overflow-ellipsis whitespace-nowrap"
									>
										{entity.label}
									</div>
								</div>
								{isEqual(entity.value, ownerId)
									? (
										<div className="text-[11px] leading-[22px] text-gray-800">
											{t('re: report sharing modal owner', 'Owner')}
										</div>
									)
									: <Button
										leftNode={<CloseCircleIcon />}
										appearance={ButtonAppearance.GHOST}
										color={ButtonColor.GRAY}
										onClick={
											() => setSelectedEntities(
												curr => curr
													.filter(selectedEntity => selectedEntity.value !== entity.value),
											)
										}
									/>
								}
							</motion.div>
						))
						}
					</AnimatePresence>
				</div>
			</Dialog.Content>
			<Dialog.Footer>
				<Button
					title={t('sc: discard changes', 'Discard changes')}
					leftNode={<BanIcon />}
					appearance={ButtonAppearance.GHOST}
					color={ButtonColor.RED}
					onClick={close}
					data-testid="discard-sharing-changes"
				/>
				<Button
					title={t('sc: confirm', 'Confirm')}
					leftNode={<BackArrowIcon />}
					loading={saving}
					disabled={selectedEntities.length === 0 || saving}
					onClick={saveReportSharing}
					data-testid="save-sharing-changes"
				/>
			</Dialog.Footer>
		</Dialog>
	);
};

export default SpotifyReportSharingModal;
